// LazyVideo — background video whose source is attached only when near the
// viewport. Keeps the page fast on mobile (heavy mp4s are deferred until needed).
// The <video> element is always present (so testids/attrs exist immediately);
// the <source> is appended via the DOM ref, so there is no setState-in-effect.
import React, { useEffect, useRef } from "react";

export default function LazyVideo({ src, eager = false, fit = "cover", testId, radius = 0 }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const load = () => {
      if (v.dataset.loaded) return;
      v.dataset.loaded = "1";
      const source = document.createElement("source");
      source.src = src;
      source.type = "video/mp4";
      v.appendChild(source);
      v.load();
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    if (eager || typeof IntersectionObserver === "undefined") { load(); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { load(); io.disconnect(); } }),
      { rootMargin: "500px 0px" }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [src, eager]);

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: radius }}>
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload={eager ? "auto" : "none"}
        disablePictureInPicture
        aria-hidden="true"
        data-testid={testId}
        style={{ width: "100%", height: "100%", objectFit: fit, display: "block" }}
      />
    </div>
  );
}
